import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PRODUCTS, GET_MAIN_CATEGORIES, DELETE_PRODUCT, UPDATE_PRODUCT_ORDERS, UPDATE_PRODUCT } from '../../apollo/queries';
import '../css/ProductManagementPage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

// DnD Kit imports
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 제품 이미지 가져오기 함수
const getProductImage = (images) => {
    if (!images) {
        return null; // null을 반환하면 ImageWithPlaceholder가 SVG 표시
    }
    
    try {
        if (typeof images === 'string') {
            const cleanedImages = images.trim();
            
            // 빈 문자열이나 빈 배열 체크
            if (cleanedImages === '' || cleanedImages === '[]') {
                return null;
            }
            
            // JSON 배열인 경우 파싱
            if (cleanedImages.startsWith('[')) {
                const parsedImages = JSON.parse(cleanedImages);
                if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                    return parsedImages[0];
                }
                return null;
            }
            
            // 단일 URL 문자열인 경우
            if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                return cleanedImages;
            }
        } else if (Array.isArray(images) && images.length > 0) {
            return images[0];
        }
    } catch (e) {
        console.error('이미지 파싱 에러:', e, 'images:', images);
    }
    
    return null;
};

// Sortable Product Item Component (div 기반으로 변경)
const SortableProductItem = ({ product, index, onEdit, isDisplayMode = false }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: product.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };



    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR');
    };

    if (isDisplayMode) {
        return (
            <div
                ref={setNodeRef}
                style={style}
                className={`product-item ${isDragging ? 'dragging' : ''}`}
                {...attributes}
            >
                <div className="product-drag-handle" {...listeners}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 6h8v2H8V6zm0 4h8v2H8v-2zm0 4h8v2H8v-2z" fill="#666"/>
                    </svg>
                </div>
                <div className="product-image">
                    <ImageWithPlaceholder 
                        src={getProductImage(product.images)} 
                        alt={product.name} 
                        className="admin-product-image"
                        style={{ width: '60px', height: '40px' }}
                    />
                </div>
                <div className="product-info">
                    <div className="product-name">{product.name}</div>
                    <div className="product-details">
                        <span>코드: {product.productCode || product.id}</span> | 
                        <span>{product.mainCategory?.name}</span>
                        {product.subCategory && <span> &gt; {product.subCategory.name}</span>} | 
                        <span>사이즈: {product.size || '미입력'}</span>
                    </div>
                </div>
                <div className="product-actions">
                    <button 
                        className="btn-edit-product" 
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(product.id);
                        }}
                    >
                        수정
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

const ProductManagementPage = () => {
    const navigate = useNavigate();
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [isDisplayMode, setIsDisplayMode] = useState(false);
    const [selectedCategoryForDisplay, setSelectedCategoryForDisplay] = useState('');
    const [selectedSubCategoryForDisplay, setSelectedSubCategoryForDisplay] = useState('');
    const [filters, setFilters] = useState({
        searchType: 'name',
        searchQuery: '',
        mainCategoryId: '',
        subCategoryId: '',
        isActive: null,
        dateFrom: '',
        dateTo: '',
        displayStatus: 'all' // all, active, inactive
    });

    // DnD Kit sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // GraphQL 쿼리
    const { data: productsData, loading: productsLoading, error: productsError, refetch: refetchProducts } = useQuery(GET_PRODUCTS, {
        variables: {
            // 상품 진열 관리 모드에서는 활성화된 제품만 표시
            isActive: isDisplayMode ? true : (filters.displayStatus === 'all' ? null : filters.displayStatus === 'active'),
            mainCategoryId: isDisplayMode ? selectedCategoryForDisplay || null : filters.mainCategoryId || null,
            subCategoryId: isDisplayMode ? selectedSubCategoryForDisplay || null : filters.subCategoryId || null,
            limit: 1000
        },
        errorPolicy: 'all'
    });

    const { data: categoriesData } = useQuery(GET_MAIN_CATEGORIES, {
        errorPolicy: 'all'
    });

    // 삭제 뮤테이션 (soft delete - isActive를 false로 설정)
    const [deleteProduct] = useMutation(DELETE_PRODUCT, {
        onCompleted: () => {
            refetchProducts();
            alert('제품이 비활성화되었습니다.');
        },
        onError: (error) => {
            alert(`제품 비활성화 실패: ${error.message}`);
        }
    });

    // 제품 업데이트 뮤테이션
    const [updateProduct] = useMutation(UPDATE_PRODUCT, {
        onCompleted: () => {
            refetchProducts();
        },
        onError: (error) => {
            console.error('제품 업데이트 에러:', error);
            alert(`제품 업데이트 실패: ${error.message}`);
        }
    });

    // 제품 순서 업데이트 뮤테이션
    const [updateProductOrders] = useMutation(UPDATE_PRODUCT_ORDERS, {
        onCompleted: (data) => {
            console.log('=== 뮤테이션 성공 ===');
            console.log('서버 응답 데이터:', data);
            console.log('업데이트된 제품들:', data.updateProductOrders);
            refetchProducts();
            console.log('제품 순서가 성공적으로 업데이트되었습니다.');
        },
        onError: (error) => {
            console.error('=== 뮤테이션 에러 ===');
            console.error('에러 상세:', error);
            console.error('에러 메시지:', error.message);
            console.error('GraphQL 에러:', error.graphQLErrors);
            console.error('네트워크 에러:', error.networkError);
            alert(`제품 순서 업데이트 실패: ${error.message}`);
        }
    });

    const products = productsData?.products?.products || []; // 페이지네이션 구조에 맞게 수정
    
    // 검색 필터 적용
    const filteredProducts = products.filter(product => {
        // 검색어 필터
        if (filters.searchQuery) {
            const searchLower = filters.searchQuery.toLowerCase();
            if (filters.searchType === 'name') {
                if (!product.name?.toLowerCase().includes(searchLower)) {
                    return false;
                }
            } else if (filters.searchType === 'id') {
                if (!product.id.toString().includes(filters.searchQuery)) {
                    return false;
                }
            }
        }
        
        // 카테고리 필터 (일반 모드에서만)
        if (!isDisplayMode && filters.mainCategoryId) {
            if (product.mainCategoryId !== filters.mainCategoryId) {
                return false;
            }
        }
        
        if (!isDisplayMode && filters.subCategoryId) {
            if (product.subCategoryId !== filters.subCategoryId) {
                return false;
            }
        }
        
        // 활성화 상태 필터
        // 상품 진열 관리 모드에서는 항상 활성화된 제품만 표시
        if (isDisplayMode) {
            if (product.isActive !== true) {
                return false;
            }
        } else if (filters.isActive !== null) {
            if (product.isActive !== filters.isActive) {
                return false;
            }
        }
        
        // 날짜 필터
        if (filters.dateFrom) {
            const productDate = new Date(product.createdAt);
            const fromDate = new Date(filters.dateFrom);
            if (productDate < fromDate) {
                return false;
            }
        }
        
        if (filters.dateTo) {
            const productDate = new Date(product.createdAt);
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999); // 종료일의 끝까지 포함
            if (productDate > toDate) {
                return false;
            }
        }
        
        return true;
    });
    
    // 진열 관리 모드에서는 order 순으로 정렬
    const sortedProducts = isDisplayMode 
        ? [...filteredProducts].sort((a, b) => (a.order || 0) - (b.order || 0))
        : filteredProducts;
    const categories = categoriesData?.mainCategories || [];

    // 제품 데이터 변경 시 로깅
    React.useEffect(() => {
        if (products.length > 0) {
            console.log('=== 제품 데이터 업데이트 ===');
            console.log('원본 products:', products.map(p => ({ id: p.id, name: p.name, order: p.order })));
            console.log('sortedProducts:', sortedProducts.map(p => ({ id: p.id, name: p.name, order: p.order })));
            console.log('=== 제품 데이터 업데이트 끝 ===');
        }
    }, [products, sortedProducts]);

    // 드래그 앤 드롭 핸들러
    const handleDragEnd = (event) => {
        const { active, over } = event;
        
        console.log('=== 드래그 앤 드롭 디버깅 시작 ===');
        console.log('active:', active);
        console.log('over:', over);
        console.log('sortedProducts:', sortedProducts);
        console.log('sortedProducts 길이:', sortedProducts.length);

        if (active.id !== over?.id) {
            const oldIndex = sortedProducts.findIndex(product => product.id === active.id);
            const newIndex = sortedProducts.findIndex(product => product.id === over.id);
            
            console.log('oldIndex:', oldIndex);
            console.log('newIndex:', newIndex);
            console.log('active.id:', active.id);
            console.log('over.id:', over.id);

            const reorderedProducts = arrayMove(sortedProducts, oldIndex, newIndex);
            console.log('reorderedProducts:', reorderedProducts);
            
            const productOrders = reorderedProducts.map((product, index) => ({
                id: product.id,
                order: index
            }));
            
            console.log('productOrders:', productOrders);

            updateProductOrders({
                variables: { productOrders }
            });
        } else {
            console.log('드래그 앤 드롭 취소 (같은 위치)');
        }
        
        console.log('=== 드래그 앤 드롭 디버깅 끝 ===');
    };

    // 사이드바 메뉴 클릭 핸들러
    const handleSidebarMenuClick = (menuType) => {
        if (menuType === 'display') {
            setIsDisplayMode(true);
            console.log('상품 진열 관리 모드 활성화');
        } else {
            setIsDisplayMode(false);
            console.log('전체 상품 관리 모드 활성화');
        }
    };

    // 카테고리 변경 핸들러
    const handleCategoryChangeForDisplay = (categoryId) => {
        setSelectedCategoryForDisplay(categoryId);
        setSelectedSubCategoryForDisplay(''); // 서브카테고리 초기화
    };

    const handleSubCategoryChangeForDisplay = (subCategoryId) => {
        setSelectedSubCategoryForDisplay(subCategoryId);
    };

    // 선택된 메인 카테고리의 서브카테고리들
    const selectedMainCategory = categories.find(cat => cat.id === selectedCategoryForDisplay);
    const subCategories = selectedMainCategory?.subCategories || [];

    // 필터 변경 핸들러
    const handleFilterChange = (name, value) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 메인 카테고리 변경 핸들러 (일반 모드용)
    const handleMainCategoryChange = (categoryId) => {
        setFilters(prev => ({
            ...prev,
            mainCategoryId: categoryId,
            subCategoryId: '' // 서브카테고리 초기화
        }));
    };

    // 서브카테고리 목록 가져오기
    const getSubCategories = () => {
        if (!filters.mainCategoryId) return [];
        const mainCategory = categories.find(cat => cat.id === filters.mainCategoryId);
        return mainCategory?.subCategories || [];
    };

    // 검색 버튼 핸들러
    const handleSearch = () => {
        // 필터는 이미 실시간으로 적용되므로, 추가적인 검색 로직은 필요 없음
        // 필요시 여기에 추가 검색 로직 구현
        console.log('검색 실행 - 현재 필터:', filters);
        // GraphQL 쿼리 리페치 (필요한 경우)
        refetchProducts();
    };

    // 활성화 상태 필터 핸들러 (라디오 버튼처럼 동작)
    const handleActiveFilterChange = (type) => {
        if (type === 'active') {
            setFilters(prev => ({
                ...prev,
                isActive: true,
                displayStatus: 'active'
            }));
        } else if (type === 'inactive') {
            setFilters(prev => ({
                ...prev,
                isActive: false,
                displayStatus: 'inactive'
            }));
        }
    };

    // 제품 진열 여부 변경 핸들러
    const handleProductActiveToggle = async (productId, newStatus, product) => {
        // 이미 같은 상태면 변경하지 않음
        if (product.isActive === newStatus) {
            return;
        }

        try {
            await updateProduct({
                variables: {
                    id: productId,
                    input: {
                        name: product.name,
                        price: parseFloat(product.price),
                        isActive: newStatus,
                        mainCategoryId: product.mainCategoryId,
                        ...(product.subCategoryId && { subCategoryId: product.subCategoryId }),
                        ...(product.description && { description: product.description }),
                        ...(product.specifications && { specifications: product.specifications }),
                        ...(product.discountPrice && { discountPrice: parseFloat(product.discountPrice) }),
                        ...(product.stock !== undefined && { stock: product.stock }),
                        ...(product.images && { images: product.images }),
                        ...(product.productCode && { productCode: product.productCode }),
                        ...(product.size && { size: product.size }),
                        ...(product.options && { options: product.options })
                    }
                }
            });
        } catch (error) {
            console.error('제품 진열 여부 변경 에러:', error);
            alert(`제품 진열 여부 변경 실패: ${error.message}`);
        }
    };

    // 날짜 단축키 핸들러
    const handleDateShortcut = (months) => {
        const today = new Date();
        const pastDate = new Date(today);
        
        if (months === 12) {
            // 전체 선택
            setFilters(prev => ({
                ...prev,
                dateFrom: '',
                dateTo: ''
            }));
        } else {
            pastDate.setMonth(today.getMonth() - months);
            setFilters(prev => ({
                ...prev,
                dateFrom: pastDate.toISOString().split('T')[0],
                dateTo: today.toISOString().split('T')[0]
            }));
        }
    };

    // 제품 선택 핸들러
    const handleProductSelect = (productId) => {
        setSelectedProducts(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
    };

    // 전체 선택 핸들러
    const handleSelectAll = () => {
        if (selectedProducts.length === sortedProducts.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(sortedProducts.map(p => p.id));
        }
    };

    // 선택 삭제 핸들러
    const handleDeleteSelected = () => {
        if (selectedProducts.length === 0) {
            alert('삭제할 제품을 선택해주세요.');
            return;
        }

        if (window.confirm(`선택한 ${selectedProducts.length}개 제품을 삭제하시겠습니까?`)) {
            // 여러 제품 삭제 로직 (현재는 단일 삭제만 지원)
            selectedProducts.forEach(productId => {
                deleteProduct({ variables: { id: productId } });
            });
            setSelectedProducts([]);
        }
    };



    // 날짜 포맷
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR');
    };

    // 로딩 상태
    if (productsLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                데이터를 불러오는 중...
            </div>
        );
    }

    // 에러 상태
    if (productsError) {
        return (
            <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
                데이터를 불러오는 중 오류가 발생했습니다: {productsError.message}
            </div>
        );
    }

    return (
        <>
            <div className="breadcrumb">
                <span>홈 &gt; 상품관리</span>
            </div>
            <div className="admin-main-content">
                <aside className="admin-sidebar">
                    <div className="sidebar-menu">
                        <h3>상품관리</h3>
                        <ul>
                            <li 
                                className={!isDisplayMode ? "active" : ""}
                                onClick={() => handleSidebarMenuClick('general')}
                                style={{ cursor: 'pointer' }}
                            >
                                전체 상품 관리
                            </li>
                            <li 
                                className={isDisplayMode ? "active" : ""}
                                onClick={() => handleSidebarMenuClick('display')}
                                style={{ cursor: 'pointer' }}
                            >
                                상품 진열 관리
                            </li>
                        </ul>
                    </div>
                </aside>
                <main className="admin-content-product">
                    <div className="page-header">
                        <div className="page-title-bar"></div>
                        <h2>{isDisplayMode ? '상품 진열 관리' : '전체 상품 관리'}</h2>
                    </div>

                    {isDisplayMode ? (
                        // 상품 진열 관리 모드
                        <div className="display-management-container">
                            <div className="category-selection">
                                <div className="filter-row">
                                    <label>메인 카테고리</label>
                                    <select 
                                        className="filter-select"
                                        value={selectedCategoryForDisplay}
                                        onChange={(e) => handleCategoryChangeForDisplay(e.target.value)}
                                    >
                                        <option value="">카테고리 선택</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {subCategories.length > 0 && (
                                    <div className="filter-row">
                                        <label>서브 카테고리</label>
                                        <select 
                                            className="filter-select"
                                            value={selectedSubCategoryForDisplay}
                                            onChange={(e) => handleSubCategoryChangeForDisplay(e.target.value)}
                                        >
                                            <option value="">전체 서브카테고리</option>
                                            {subCategories.map(subCategory => (
                                                <option key={subCategory.id} value={subCategory.id}>
                                                    {subCategory.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {selectedCategoryForDisplay && (
                                <div className="product-list-container">
                                    <div className="product-list-header">
                                        <span>총 제품 수: {sortedProducts.length} 건</span>
                                        <div className="help-text">
                                            💡 제품을 드래그하여 순서를 변경할 수 있습니다.
                                        </div>
                                    </div>
                                    
                                    <DndContext
                                        sensors={sensors}
                                        collisionDetection={closestCenter}
                                        onDragEnd={handleDragEnd}
                                    >
                                        <div className="product-list-grid">
                                            <SortableContext 
                                                items={sortedProducts.map(p => p.id)} 
                                                strategy={verticalListSortingStrategy}
                                            >
                                                {sortedProducts.length === 0 ? (
                                                    <div className="no-products-message">
                                                        {selectedCategoryForDisplay ? 
                                                            '선택한 카테고리에 등록된 제품이 없습니다.' : 
                                                            '카테고리를 선택해주세요.'
                                                        }
                                                    </div>
                                                ) : (
                                                    sortedProducts.map((product, index) => (
                                                        <SortableProductItem
                                                            key={product.id}
                                                            product={product}
                                                            index={index}
                                                            onEdit={(id) => navigate(`/admin/product/edit?id=${id}`)}
                                                            isDisplayMode={true}
                                                        />
                                                    ))
                                                )}
                                            </SortableContext>
                                        </div>
                                    </DndContext>
                                </div>
                            )}
                        </div>
                    ) : (
                        // 기존 전체 상품 관리 모드
                        <>
                            <div className="product-search-filters">
                                <div className="filter-row">
                                    <label>검색 명</label>
                                    <select 
                                        className="filter-select"
                                        value={filters.searchType}
                                        onChange={(e) => handleFilterChange('searchType', e.target.value)}
                                    >
                                        <option value="name">상품명</option>
                                        <option value="id">제품ID</option>
                                    </select>
                                    <input 
                                        type="text" 
                                        className="filter-input"
                                        placeholder="제품 정보를 기입해주세요"
                                        value={filters.searchQuery}
                                        onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                                    />
                                </div>
                                
                                <div className="filter-row">
                                    <label>기간 검색</label>
                                    <select className="filter-select">
                                        <option>최초 등록 날짜</option>
                                    </select>
                                    <input 
                                        type="date" 
                                        className="filter-date"
                                        value={filters.dateFrom}
                                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    />
                                    <span>-</span>
                                    <input 
                                        type="date" 
                                        className="filter-date"
                                        value={filters.dateTo}
                                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    />
                                    <div className="date-shortcuts">
                                        <button onClick={() => handleDateShortcut(1)}>1개월</button>
                                        <button onClick={() => handleDateShortcut(3)}>3개월</button>
                                        <button onClick={() => handleDateShortcut(12)}>전체</button>
                                    </div>
                                </div>
                                
                                <div className="filter-row">
                                    <label>카테고리</label>
                                    <select 
                                        className="filter-select"
                                        value={filters.mainCategoryId}
                                        onChange={(e) => handleMainCategoryChange(e.target.value)}
                                    >
                                        <option value="">전체 카테고리</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                    <select 
                                        className="filter-select"
                                        value={filters.subCategoryId}
                                        onChange={(e) => handleFilterChange('subCategoryId', e.target.value)}
                                    >
                                        <option value="">서브 카테고리</option>
                                        {getSubCategories().map(subCategory => (
                                            <option key={subCategory.id} value={subCategory.id}>
                                                {subCategory.name}
                                            </option>
                                        ))}
                                    </select>
                                    <select 
                                        className="filter-select"
                                        value={filters.displayStatus}
                                        onChange={(e) => handleFilterChange('displayStatus', e.target.value)}
                                    >
                                        <option value="all">상세 카테고리</option>
                                        <option value="active">진열</option>
                                        <option value="inactive">비진열</option>
                                    </select>
                                </div>
                                
                                <div className="filter-row">
                                    <label>진열 여부</label>
                                    <div className="filter-checkboxes">
                                        <label>
                                            <input 
                                                type="radio" 
                                                name="displayStatus"
                                                checked={filters.isActive === true}
                                                onChange={() => handleActiveFilterChange('active')}
                                            />
                                            진열
                                        </label>
                                        <label>
                                            <input 
                                                type="radio" 
                                                name="displayStatus"
                                                checked={filters.isActive === false}
                                                onChange={() => handleActiveFilterChange('inactive')}
                                            />
                                            비진열
                                        </label>
                                    </div>
                                </div>

                                <button className="search-button-product" onClick={handleSearch}>
                                    검색
                                </button>
                            </div>

                            <div className="product-list-container">
                                <div className="product-list-header">
                                    <span>총 제품 수: {sortedProducts.length} 건</span>
                                    <div>
                                        <button className="btn-add-product" onClick={() => navigate('/admin/product/add')}>+ 상품 등록</button>
                                    </div>
                                </div>
                                <table className="product-table">
                                    <thead>
                                        <tr>
                                            <th>
                                                <input 
                                                    type="checkbox" 
                                                    checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                                                    onChange={handleSelectAll}
                                                />
                                            </th>
                                            <th>번호</th>
                                            <th>이미지</th>
                                            <th>상품 코드</th>
                                            <th>제품 명</th>
                                            <th>사이즈</th>
                                            <th>옵션</th>
                                            <th>카테고리</th>
                                            <th>상태</th>
                                            <th>일자</th>

                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedProducts.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                                                    등록된 제품이 없습니다.
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedProducts.map((product, index) => (
                                                <tr 
                                                    key={product.id}
                                                    onClick={() => navigate(`/admin/product/edit?id=${product.id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                    className="clickable-row"
                                                >
                                                    <td>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedProducts.includes(product.id)}
                                                            onChange={() => handleProductSelect(product.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <div className="product-cell-name">
                                                            <ImageWithPlaceholder 
                                                                src={getProductImage(product.images)} 
                                                                alt={product.name} 
                                                                className="admin-product-image"
                                                                style={{ width: '60px', height: '40px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td>{product.productCode || '미입력'}</td>
                                                    <td>{product.name}</td>
                                                    <td>{product.size || '미입력'}</td>
                                                    <td>
                                                        {product.options && product.options.length > 0 
                                                            ? product.options.join(', ') 
                                                            : '옵션 없음'
                                                        }
                                                    </td>
                                                    <td>
                                                        {product.mainCategory?.name}
                                                        {product.subCategory && ` > ${product.subCategory.name}`}
                                                    </td>
                                                    <td>
                                                        <div className="display-status-control">
                                                            <label>
                                                                <input 
                                                                    type="radio" 
                                                                    name={`displayStatus-${product.id}`}
                                                                    checked={product.isActive === true}
                                                                    onChange={() => handleProductActiveToggle(product.id, true, product)}
                                                                />
                                                                진열
                                                            </label>
                                                            <label>
                                                                <input 
                                                                    type="radio" 
                                                                    name={`displayStatus-${product.id}`}
                                                                    checked={product.isActive === false}
                                                                    onChange={() => handleProductActiveToggle(product.id, false, product)}
                                                                />
                                                                비진열
                                                            </label>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>등록 {formatDate(product.createdAt)}</div>
                                                        <div>수정 {formatDate(product.updatedAt)}</div>
                                                    </td>

                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                                <div className="product-list-actions">
                                    <button className="btn-delete-selected" onClick={handleDeleteSelected}>선택 삭제</button>
                                </div>
                                <div className="pagination">
                                    <button>&lt;</button>
                                    <span>1 / 1</span>
                                    <button>&gt;</button>
                                </div>
                            </div>
                        </>
                    )}
                </main>
            </div>
        </>
    );
};

export default ProductManagementPage; 